import type { Action } from '@reduxjs/toolkit';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import { asSavedSearchGroup } from '@iconik-io/iconik-api/src/schema_patched/saved_search';
import {
  httpDeleteSearchSavedGroupByGroupId,
  httpDeleteSearchSavedGroupByGroupIdSearchBySearchId,
  httpGetSearchSavedGroups,
  httpPatchSearchSavedGroupByGroupId,
  httpPostSearchSavedGroup,
  httpPostSearchSavedGroupByGroupIdSearchBySearchId,
  httpPutSearchSavedBySearchId,
  type SearchSavedSearchGroupSchemaWithId,
  type SearchSavedSearchGroupSchemaWithIdNullable,
  type SearchSavedSearchSchema,
} from '@iconik-io/iconik-api/src/services/search';

import type { AppDispatch, GetState } from '../../store/types';
import {
  changeSavedSearchGroupSuccess,
  clearSavedSearchGroupSuccess,
  createSavedSearchGroupSuccess,
  deleteGroupSuccess,
  getSavedSearchGroupsSuccess,
  updateGroupNameSuccess,
} from './slice';

export const changeSavedSearchGroup =
  (savedSearchId: string, groupId: string) =>
  (dispatch: AppDispatch): Promise<Action | undefined> =>
    dispatch(
      httpPostSearchSavedGroupByGroupIdSearchBySearchId(
        groupId,
        savedSearchId,
        { index_immediately: true },
      ),
    ).then(() =>
      dispatch(changeSavedSearchGroupSuccess({ savedSearchId, groupId })),
    );

export const createSavedSearchGroup =
  (newGroupName: string) =>
  (dispatch: AppDispatch): Promise<SearchSavedSearchGroupSchemaWithId> =>
    dispatch(
      httpPostSearchSavedGroup(
        { name: newGroupName },
        { index_immediately: true },
      ),
    ).then(({ body: group }) => {
      dispatch(createSavedSearchGroupSuccess(group));
      return group;
    });

export const clearSavedSearchGroup =
  (savedSearchId: string, groupId: string) =>
  (dispatch: AppDispatch): Promise<Action | undefined> =>
    dispatch(
      httpDeleteSearchSavedGroupByGroupIdSearchBySearchId(
        groupId,
        savedSearchId,
      ),
    ).then(() => dispatch(clearSavedSearchGroupSuccess(savedSearchId)));

export const updateGroupName =
  (groupId: string, name: string) =>
  (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(httpPatchSearchSavedGroupByGroupId(groupId, { name })).then(
      ({ body }) => dispatch(updateGroupNameSuccess(asSavedSearchGroup(body))),
    );

export const deleteGroup =
  (groupId: string) =>
  (dispatch: AppDispatch): Promise<Action | undefined> =>
    dispatch(httpDeleteSearchSavedGroupByGroupId(groupId)).then(() =>
      dispatch(deleteGroupSuccess(groupId)),
    );

// Fetches all saved search group pages
export const getSavedSearchGroups =
  () =>
  (dispatch: AppDispatch): Promise<void> => {
    const perPage = 50;
    let groups: SearchSavedSearchGroupSchemaWithIdNullable[] = [];

    const resolve = ({
      body,
    }: EndpointResponse<ReturnType<typeof httpGetSearchSavedGroups>>):
      | Promise<SearchSavedSearchGroupSchemaWithIdNullable[]>
      | SearchSavedSearchGroupSchemaWithIdNullable[] => {
      if (!body.objects) {
        return groups;
      }
      groups = groups.concat(body.objects);
      if (body.pages > body.page) {
        return dispatch(
          httpGetSearchSavedGroups({
            per_page: perPage,
            sort: 'name,asc',
            page: body.page + 1,
          }),
        ).then(resolve);
      }
      return groups;
    };

    return dispatch(
      httpGetSearchSavedGroups({
        per_page: perPage,
        sort: 'name,asc',
      }),
    )
      .then(resolve)
      .then((allGroups) => {
        dispatch(getSavedSearchGroupsSuccess(allGroups));
      });
  };

export function ungroupSavedSearches(ids: string[]) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const savedSearches = getState().savedSearchesReducer.savedSearches;
    const savedSearchesToMove = savedSearches.filter(
      (savedSearch) => ids.includes(savedSearch.id) && savedSearch.group_id,
    );
    if (savedSearchesToMove.length < 1) {
      return Promise.resolve();
    }
    const savedSearchesToMovePromises: Promise<unknown>[] = [];
    savedSearchesToMove.forEach((savedSearch) => {
      const newSavedSearch = {
        ...savedSearch,
        group_id: null,
      };
      savedSearchesToMovePromises.push(
        dispatch(
          httpPutSearchSavedBySearchId(
            newSavedSearch.id,
            // biome-ignore lint/plugin: see the criteria note in schema_patched/saved_search.ts.
            newSavedSearch as SearchSavedSearchSchema,
          ),
        ),
      );
      if (savedSearch.group_id) {
        savedSearchesToMovePromises.push(
          dispatch(
            httpDeleteSearchSavedGroupByGroupIdSearchBySearchId(
              savedSearch.group_id,
              savedSearch.id,
            ),
          ),
        );
      }
    });
    return Promise.all(savedSearchesToMovePromises).then(() => {
      savedSearchesToMove.forEach((savedSearch) => {
        dispatch(clearSavedSearchGroupSuccess(savedSearch.id));
      });
    });
  };
}
